import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import Loader from './components/Loader';
import './SearchTargetPipelineView.css';

// Type definitions for pipeline data
interface PipelineSummary {
    attempts: { total: number; completed: number; failed: number };
    results: { total: number; validated: number; withPdfs: number };
    candidates: { total: number; processed: number; failed: number; pdfs: number };
    reportSources: { total: number };
    bonds: { total: number };
}

interface PipelineAttempt {
    _id: string;
    engine: string;
    status: string;
    result_count: number;
    search_timestamp?: string;
}

interface SearchTarget {
    _id: string;
    query: string;
    issuer?: string;
    identifier?: string;
    category?: string;
    country?: string;
}

interface PipelineResult {
    _id: string;
    url: string;
    pdf_urls_count: number;
    validated: boolean;
    checking_failed: boolean;
    timestamp?: string;
}

interface PipelineCandidate {
    _id: string;
    url: string;
    is_pdf: boolean;
    processed: boolean;
    failed: boolean;
    crawler_engine?: string;
    timestamp?: string;
}

interface PipelineReportSource {
    _id: string;
    filename?: string;
    url?: string;
    isin?: string;
    title?: string;
    document_type?: string;
    download_timestamp?: string;
    extracted_timestamp?: string;
}

interface PipelineBond {
    _id: string;
    isin: string;
    issuer?: string;
    bond_type?: string;
    volume?: string;
    currency?: string;
    issue_date?: string;
    maturity_date?: string;
}

interface NodeData<T> {
    items: T[];
    loading: boolean;
    loaded: boolean;
    page: number;
    totalPages: number;
    total: number;
}

// Helper to truncate URLs
const truncateUrl = (url: string, maxLength = 60): string => {
    if (!url || url.length <= maxLength) return url || '-';
    return url.substring(0, maxLength) + '...';
};

// Get engine badge class
const getEngineBadgeClass = (engine?: string): string => {
    switch (engine?.toLowerCase()) {
        case 'bing': return 'bing';
        case 'google': return 'google';
        case 'brave': return 'brave';
        case 'duckduckgo': return 'duckduckgo';
        default: return 'default';
    }
};

const SearchTargetPipelineView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { token, authLoaded } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const apiBaseUrl = env.apiUrl;
    const apiKey = env.apiKey;

    // Main state
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTarget, setSearchTarget] = useState<SearchTarget | null>(null);
    const [summary, setSummary] = useState<PipelineSummary | null>(null);
    const [attempts, setAttempts] = useState<PipelineAttempt[]>([]);

    // Expandable tree state
    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
    const [resultsExpanded, setResultsExpanded] = useState(false);

    // Lazy-loaded data with caching
    const [resultsData, setResultsData] = useState<NodeData<PipelineResult>>({
        items: [], loading: false, loaded: false, page: 1, totalPages: 1, total: 0
    });
    const [candidatesData, setCandidatesData] = useState<Record<string, NodeData<PipelineCandidate>>>({});
    const [reportSourcesData, setReportSourcesData] = useState<Record<string, NodeData<PipelineReportSource>>>({});
    const [bondsData, setBondsData] = useState<Record<string, NodeData<PipelineBond>>>({});

    // Create headers helper
    const getHeaders = useCallback(() => {
        const headers: Record<string, string> = {
            'x-api-key': apiKey,
            'Content-Type': 'application/json'
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        return headers;
    }, [apiKey, token]);

    // Fetch initial pipeline summary
    const fetchPipelineSummary = useCallback(async () => {
        if (!id) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${apiBaseUrl}/search-targets/${id}/pipeline`, {
                headers: getHeaders()
            });

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error(t('pipeline.targetNotFound'));
                }
                throw new Error(t('pipeline.fetchError'));
            }

            const data = await response.json();
            setSearchTarget(data.searchTarget);
            setSummary(data.summary);
            setAttempts(data.attempts || []);
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : t('common.unknownError');
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [id, apiBaseUrl, getHeaders, t]);

    // Fetch search results (level 1)
    const fetchResults = useCallback(async (page = 1) => {
        if (!id || resultsData.loading) return;

        setResultsData(prev => ({ ...prev, loading: true }));

        try {
            const response = await fetch(
                `${apiBaseUrl}/search-targets/${id}/pipeline/results?page=${page}&limit=20`,
                { headers: getHeaders() }
            );

            if (!response.ok) throw new Error(t('pipeline.fetchError'));

            const data = await response.json();
            setResultsData(prev => ({
                items: page === 1 ? data.results : [...prev.items, ...data.results],
                loading: false,
                loaded: true,
                page: data.pagination.page,
                totalPages: data.pagination.totalPages,
                total: data.pagination.total
            }));
        } catch (err) {
            setResultsData(prev => ({ ...prev, loading: false }));
        }
    }, [id, apiBaseUrl, getHeaders, t, resultsData.loading]);

    // Fetch candidates for a result (level 2)
    const fetchCandidates = useCallback(async (resultId: string, page = 1) => {
        if (candidatesData[resultId]?.loading) return;

        setCandidatesData(prev => ({
            ...prev,
            [resultId]: { ...prev[resultId], loading: true, items: prev[resultId]?.items || [], loaded: prev[resultId]?.loaded || false, page: 1, totalPages: 1, total: 0 }
        }));

        try {
            const response = await fetch(
                `${apiBaseUrl}/pipeline/search-results/${resultId}/candidates?page=${page}&limit=20`,
                { headers: getHeaders() }
            );

            if (!response.ok) throw new Error(t('pipeline.fetchError'));

            const data = await response.json();
            setCandidatesData(prev => ({
                ...prev,
                [resultId]: {
                    items: page === 1 ? data.candidates : [...(prev[resultId]?.items || []), ...data.candidates],
                    loading: false,
                    loaded: true,
                    page: data.pagination.page,
                    totalPages: data.pagination.totalPages,
                    total: data.pagination.total
                }
            }));
        } catch (err) {
            setCandidatesData(prev => ({
                ...prev,
                [resultId]: { ...prev[resultId], loading: false }
            }));
        }
    }, [apiBaseUrl, getHeaders, t, candidatesData]);

    // Fetch report sources for a candidate (level 3)
    const fetchReportSources = useCallback(async (candidateId: string, page = 1) => {
        if (reportSourcesData[candidateId]?.loading) return;

        setReportSourcesData(prev => ({
            ...prev,
            [candidateId]: { ...prev[candidateId], loading: true, items: prev[candidateId]?.items || [], loaded: prev[candidateId]?.loaded || false, page: 1, totalPages: 1, total: 0 }
        }));

        try {
            const response = await fetch(
                `${apiBaseUrl}/pipeline/candidates/${candidateId}/report-sources?page=${page}&limit=20`,
                { headers: getHeaders() }
            );

            if (!response.ok) throw new Error(t('pipeline.fetchError'));

            const data = await response.json();
            setReportSourcesData(prev => ({
                ...prev,
                [candidateId]: {
                    items: page === 1 ? data.reportSources : [...(prev[candidateId]?.items || []), ...data.reportSources],
                    loading: false,
                    loaded: true,
                    page: data.pagination.page,
                    totalPages: data.pagination.totalPages,
                    total: data.pagination.total
                }
            }));
        } catch (err) {
            setReportSourcesData(prev => ({
                ...prev,
                [candidateId]: { ...prev[candidateId], loading: false }
            }));
        }
    }, [apiBaseUrl, getHeaders, t, reportSourcesData]);

    // Fetch bonds for a report source (level 4)
    const fetchBonds = useCallback(async (reportSourceId: string, page = 1) => {
        if (bondsData[reportSourceId]?.loading) return;

        setBondsData(prev => ({
            ...prev,
            [reportSourceId]: { ...prev[reportSourceId], loading: true, items: prev[reportSourceId]?.items || [], loaded: prev[reportSourceId]?.loaded || false, page: 1, totalPages: 1, total: 0 }
        }));

        try {
            const response = await fetch(
                `${apiBaseUrl}/pipeline/report-sources/${reportSourceId}/bonds?page=${page}&limit=20`,
                { headers: getHeaders() }
            );

            if (!response.ok) throw new Error(t('pipeline.fetchError'));

            const data = await response.json();
            setBondsData(prev => ({
                ...prev,
                [reportSourceId]: {
                    items: page === 1 ? data.bonds : [...(prev[reportSourceId]?.items || []), ...data.bonds],
                    loading: false,
                    loaded: true,
                    page: data.pagination.page,
                    totalPages: data.pagination.totalPages,
                    total: data.pagination.total
                }
            }));
        } catch (err) {
            setBondsData(prev => ({
                ...prev,
                [reportSourceId]: { ...prev[reportSourceId], loading: false }
            }));
        }
    }, [apiBaseUrl, getHeaders, t, bondsData]);

    // Initial load
    useEffect(() => {
        if (authLoaded && id) {
            fetchPipelineSummary();
        }
    }, [authLoaded, id, fetchPipelineSummary]);

    // Toggle node expansion
    const toggleNode = (nodeKey: string, level: number, itemId: string) => {
        const isExpanded = expandedNodes.has(nodeKey);

        if (isExpanded) {
            // Collapse
            setExpandedNodes(prev => {
                const next = new Set(prev);
                next.delete(nodeKey);
                return next;
            });
        } else {
            // Expand and fetch data if needed
            setExpandedNodes(prev => new Set(prev).add(nodeKey));

            // Fetch data based on level
            if (level === 1 && !candidatesData[itemId]?.loaded) {
                fetchCandidates(itemId);
            } else if (level === 2 && !reportSourcesData[itemId]?.loaded) {
                fetchReportSources(itemId);
            } else if (level === 3 && !bondsData[itemId]?.loaded) {
                fetchBonds(itemId);
            }
        }
    };

    // Toggle results section
    const toggleResultsSection = () => {
        if (!resultsExpanded && !resultsData.loaded) {
            fetchResults();
        }
        setResultsExpanded(!resultsExpanded);
    };

    const handleBack = () => {
        navigate(-1);
    };

    if (!authLoaded || loading) {
        return <Loader text={t('common.loading')} />;
    }

    if (error) {
        return (
            <div className="pipeline-view">
                <div className="error-message" style={{ color: 'red' }}>
                    {error}
                </div>
                <button onClick={handleBack} className="btn btn-primary" style={{ marginTop: '1rem' }}>
                    {t('buttons.back')}
                </button>
            </div>
        );
    }

    if (!searchTarget || !summary) {
        return (
            <div className="pipeline-view">
                <div className="pipeline-empty">{t('pipeline.targetNotFound')}</div>
                <button onClick={handleBack} className="btn btn-primary" style={{ marginTop: '1rem' }}>
                    {t('buttons.back')}
                </button>
            </div>
        );
    }

    return (
        <div className="pipeline-view">
            {/* Header */}
            <div className="pipeline-header">
                <h1>
                    {t('pipeline.title')}: {searchTarget.issuer || searchTarget.query}
                </h1>
                <div className="pipeline-header-actions">
                    <button onClick={handleBack} className="btn btn-secondary">
                        <span className="material-icons">arrow_back</span>
                        {t('buttons.back')}
                    </button>
                </div>
            </div>

            {/* Summary Bar */}
            <div className="pipeline-summary-bar">
                <div className="pipeline-summary-item">
                    <span className="material-icons">search</span>
                    <span className="count">{summary.attempts.total}</span>
                    <span className="label">{t('pipeline.attempts')}</span>
                </div>
                <div className="pipeline-summary-item">
                    <span className="material-icons">link</span>
                    <span className="count">{summary.results.total}</span>
                    <span className="label">{t('pipeline.results')}</span>
                </div>
                <div className="pipeline-summary-item">
                    <span className="material-icons">source</span>
                    <span className="count">{summary.candidates.total}</span>
                    <span className="label">{t('pipeline.candidates')}</span>
                </div>
                <div className="pipeline-summary-item">
                    <span className="material-icons">description</span>
                    <span className="count">{summary.reportSources.total}</span>
                    <span className="label">{t('pipeline.documents')}</span>
                </div>
                <div className="pipeline-summary-item">
                    <span className="material-icons">account_balance</span>
                    <span className="count">{summary.bonds.total}</span>
                    <span className="label">{t('pipeline.bonds')}</span>
                </div>
            </div>

            {/* Attempts Info Bar */}
            {attempts.length > 0 && (
                <div className="pipeline-attempts-bar">
                    <span className="material-icons">info</span>
                    <span>{t('pipeline.attemptsInfo')}:</span>
                    {attempts.map(attempt => (
                        <span
                            key={attempt._id}
                            className={`pipeline-attempt-badge ${getEngineBadgeClass(attempt.engine)}`}
                        >
                            {attempt.engine}
                            <span className="material-icons">
                                {attempt.status === 'completed' ? 'check' : attempt.status === 'failed' ? 'close' : 'hourglass_empty'}
                            </span>
                        </span>
                    ))}
                </div>
            )}

            {/* Results Tree */}
            <div className="pipeline-tree">
                <div
                    className="pipeline-tree-header"
                    onClick={toggleResultsSection}
                >
                    <h2>
                        <span className="material-icons">
                            {resultsExpanded ? 'expand_more' : 'chevron_right'}
                        </span>
                        {t('pipeline.searchResults')} ({summary.results.total})
                    </h2>
                </div>

                {resultsExpanded && (
                    <div className="pipeline-tree-content">
                        {resultsData.loading && resultsData.items.length === 0 && (
                            <div className="pipeline-node-loading">
                                <span className="material-icons">hourglass_empty</span>
                                {t('common.loading')}...
                            </div>
                        )}

                        {resultsData.items.map(result => (
                            <ResultNode
                                key={result._id}
                                result={result}
                                expandedNodes={expandedNodes}
                                toggleNode={toggleNode}
                                candidatesData={candidatesData}
                                reportSourcesData={reportSourcesData}
                                bondsData={bondsData}
                                fetchCandidates={fetchCandidates}
                                fetchReportSources={fetchReportSources}
                                fetchBonds={fetchBonds}
                                t={t}
                            />
                        ))}

                        {resultsData.loaded && resultsData.page < resultsData.totalPages && (
                            <button
                                className="pipeline-load-more"
                                onClick={() => fetchResults(resultsData.page + 1)}
                                disabled={resultsData.loading}
                            >
                                <span className="material-icons">add</span>
                                {t('pipeline.loadMore')} ({resultsData.items.length} / {resultsData.total})
                            </button>
                        )}

                        {resultsData.loaded && resultsData.items.length === 0 && (
                            <div className="pipeline-empty">{t('pipeline.noResults')}</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// Result Node Component (Level 1)
interface ResultNodeProps {
    result: PipelineResult;
    expandedNodes: Set<string>;
    toggleNode: (nodeKey: string, level: number, itemId: string) => void;
    candidatesData: Record<string, NodeData<PipelineCandidate>>;
    reportSourcesData: Record<string, NodeData<PipelineReportSource>>;
    bondsData: Record<string, NodeData<PipelineBond>>;
    fetchCandidates: (resultId: string, page?: number) => void;
    fetchReportSources: (candidateId: string, page?: number) => void;
    fetchBonds: (reportSourceId: string, page?: number) => void;
    t: (key: string) => string;
}

const ResultNode: React.FC<ResultNodeProps> = ({
    result,
    expandedNodes,
    toggleNode,
    candidatesData,
    reportSourcesData,
    bondsData,
    fetchCandidates,
    fetchReportSources,
    fetchBonds,
    t
}) => {
    const nodeKey = `result-${result._id}`;
    const isExpanded = expandedNodes.has(nodeKey);
    const candidates = candidatesData[result._id];

    return (
        <div className="pipeline-node">
            <div
                className={`pipeline-node-row pipeline-node-level-0 ${isExpanded ? 'expanded' : ''}`}
                onClick={() => toggleNode(nodeKey, 1, result._id)}
            >
                <span className={`pipeline-node-toggle ${isExpanded ? 'expanded' : ''}`}>
                    <span className="material-icons">chevron_right</span>
                </span>
                <span className="pipeline-node-icon level-results">
                    <span className="material-icons">link</span>
                </span>
                <div className="pipeline-node-content">
                    <div className="pipeline-node-title">{truncateUrl(result.url)}</div>
                    <div className="pipeline-node-meta">
                        {result.pdf_urls_count > 0 && (
                            <span>{result.pdf_urls_count} PDFs</span>
                        )}
                    </div>
                </div>
                {result.validated && (
                    <span className="pipeline-status-badge success">
                        <span className="material-icons" style={{ fontSize: '0.9rem' }}>check</span>
                    </span>
                )}
                {result.checking_failed && (
                    <span className="pipeline-status-badge error">
                        <span className="material-icons" style={{ fontSize: '0.9rem' }}>error</span>
                    </span>
                )}
            </div>

            {isExpanded && (
                <div className="pipeline-node-children">
                    {candidates?.loading && candidates.items.length === 0 && (
                        <div className="pipeline-node-loading">
                            <span className="material-icons">hourglass_empty</span>
                            {t('common.loading')}...
                        </div>
                    )}

                    {candidates?.items.map(candidate => (
                        <CandidateNode
                            key={candidate._id}
                            candidate={candidate}
                            expandedNodes={expandedNodes}
                            toggleNode={toggleNode}
                            reportSourcesData={reportSourcesData}
                            bondsData={bondsData}
                            fetchReportSources={fetchReportSources}
                            fetchBonds={fetchBonds}
                            t={t}
                        />
                    ))}

                    {candidates?.loaded && candidates.page < candidates.totalPages && (
                        <button
                            className="pipeline-load-more"
                            onClick={(e) => {
                                e.stopPropagation();
                                fetchCandidates(result._id, candidates.page + 1);
                            }}
                            disabled={candidates.loading}
                        >
                            <span className="material-icons">add</span>
                            {t('pipeline.loadMore')} ({candidates.items.length} / {candidates.total})
                        </button>
                    )}

                    {candidates?.loaded && candidates.items.length === 0 && (
                        <div className="pipeline-empty">{t('pipeline.noCandidates')}</div>
                    )}
                </div>
            )}
        </div>
    );
};

// Candidate Node Component (Level 2)
interface CandidateNodeProps {
    candidate: PipelineCandidate;
    expandedNodes: Set<string>;
    toggleNode: (nodeKey: string, level: number, itemId: string) => void;
    reportSourcesData: Record<string, NodeData<PipelineReportSource>>;
    bondsData: Record<string, NodeData<PipelineBond>>;
    fetchReportSources: (candidateId: string, page?: number) => void;
    fetchBonds: (reportSourceId: string, page?: number) => void;
    t: (key: string) => string;
}

const CandidateNode: React.FC<CandidateNodeProps> = ({
    candidate,
    expandedNodes,
    toggleNode,
    reportSourcesData,
    bondsData,
    fetchReportSources,
    fetchBonds,
    t
}) => {
    const nodeKey = `candidate-${candidate._id}`;
    const isExpanded = expandedNodes.has(nodeKey);
    const reportSources = reportSourcesData[candidate._id];

    return (
        <div className="pipeline-node">
            <div
                className={`pipeline-node-row pipeline-node-level-1 ${isExpanded ? 'expanded' : ''}`}
                onClick={() => toggleNode(nodeKey, 2, candidate._id)}
            >
                <span className={`pipeline-node-toggle ${isExpanded ? 'expanded' : ''}`}>
                    <span className="material-icons">chevron_right</span>
                </span>
                <span className="pipeline-node-icon level-candidates">
                    <span className="material-icons">source</span>
                </span>
                <div className="pipeline-node-content">
                    <div className="pipeline-node-title">{truncateUrl(candidate.url)}</div>
                    <div className="pipeline-node-meta">
                        {candidate.crawler_engine && <span>{candidate.crawler_engine}</span>}
                    </div>
                </div>
                {candidate.is_pdf && (
                    <span className="pipeline-status-badge pdf">PDF</span>
                )}
                {candidate.processed && (
                    <span className="pipeline-status-badge success">
                        <span className="material-icons" style={{ fontSize: '0.9rem' }}>check</span>
                    </span>
                )}
                {candidate.failed && (
                    <span className="pipeline-status-badge error">
                        <span className="material-icons" style={{ fontSize: '0.9rem' }}>error</span>
                    </span>
                )}
                <div className="pipeline-node-actions">
                    <Link
                        to={`/report-source-candidates/${candidate._id}`}
                        className="pipeline-node-link"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {t('buttons.view')}
                    </Link>
                </div>
            </div>

            {isExpanded && (
                <div className="pipeline-node-children">
                    {reportSources?.loading && reportSources.items.length === 0 && (
                        <div className="pipeline-node-loading">
                            <span className="material-icons">hourglass_empty</span>
                            {t('common.loading')}...
                        </div>
                    )}

                    {reportSources?.items.map(source => (
                        <ReportSourceNode
                            key={source._id}
                            source={source}
                            expandedNodes={expandedNodes}
                            toggleNode={toggleNode}
                            bondsData={bondsData}
                            fetchBonds={fetchBonds}
                            t={t}
                        />
                    ))}

                    {reportSources?.loaded && reportSources.page < reportSources.totalPages && (
                        <button
                            className="pipeline-load-more"
                            onClick={(e) => {
                                e.stopPropagation();
                                fetchReportSources(candidate._id, reportSources.page + 1);
                            }}
                            disabled={reportSources.loading}
                        >
                            <span className="material-icons">add</span>
                            {t('pipeline.loadMore')} ({reportSources.items.length} / {reportSources.total})
                        </button>
                    )}

                    {reportSources?.loaded && reportSources.items.length === 0 && (
                        <div className="pipeline-empty">{t('pipeline.noDocuments')}</div>
                    )}
                </div>
            )}
        </div>
    );
};

// Report Source Node Component (Level 3)
interface ReportSourceNodeProps {
    source: PipelineReportSource;
    expandedNodes: Set<string>;
    toggleNode: (nodeKey: string, level: number, itemId: string) => void;
    bondsData: Record<string, NodeData<PipelineBond>>;
    fetchBonds: (reportSourceId: string, page?: number) => void;
    t: (key: string) => string;
}

const ReportSourceNode: React.FC<ReportSourceNodeProps> = ({
    source,
    expandedNodes,
    toggleNode,
    bondsData,
    fetchBonds,
    t
}) => {
    const nodeKey = `source-${source._id}`;
    const isExpanded = expandedNodes.has(nodeKey);
    const bonds = bondsData[source._id];

    return (
        <div className="pipeline-node">
            <div
                className={`pipeline-node-row pipeline-node-level-2 ${isExpanded ? 'expanded' : ''}`}
                onClick={() => toggleNode(nodeKey, 3, source._id)}
            >
                <span className={`pipeline-node-toggle ${isExpanded ? 'expanded' : ''}`}>
                    <span className="material-icons">chevron_right</span>
                </span>
                <span className="pipeline-node-icon level-documents">
                    <span className="material-icons">description</span>
                </span>
                <div className="pipeline-node-content">
                    <div className="pipeline-node-title">
                        {source.filename || source.title || truncateUrl(source.url || '', 40)}
                    </div>
                    <div className="pipeline-node-meta">
                        {source.isin && <span>ISIN: {source.isin}</span>}
                        {source.document_type && <span>{source.document_type}</span>}
                    </div>
                </div>
                <div className="pipeline-node-actions">
                    <Link
                        to={`/report-sources?id=${source._id}`}
                        className="pipeline-node-link"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {t('buttons.view')}
                    </Link>
                </div>
            </div>

            {isExpanded && (
                <div className="pipeline-node-children">
                    {bonds?.loading && bonds.items.length === 0 && (
                        <div className="pipeline-node-loading">
                            <span className="material-icons">hourglass_empty</span>
                            {t('common.loading')}...
                        </div>
                    )}

                    {bonds?.items.map(bond => (
                        <BondNode key={bond._id} bond={bond} t={t} />
                    ))}

                    {bonds?.loaded && bonds.page < bonds.totalPages && (
                        <button
                            className="pipeline-load-more"
                            onClick={(e) => {
                                e.stopPropagation();
                                fetchBonds(source._id, bonds.page + 1);
                            }}
                            disabled={bonds.loading}
                        >
                            <span className="material-icons">add</span>
                            {t('pipeline.loadMore')} ({bonds.items.length} / {bonds.total})
                        </button>
                    )}

                    {bonds?.loaded && bonds.items.length === 0 && (
                        <div className="pipeline-empty">{t('pipeline.noBonds')}</div>
                    )}
                </div>
            )}
        </div>
    );
};

// Bond Node Component (Level 4 - leaf node)
interface BondNodeProps {
    bond: PipelineBond;
    t: (key: string) => string;
}

const BondNode: React.FC<BondNodeProps> = ({ bond, t }) => {
    return (
        <div className="pipeline-node">
            <div className="pipeline-node-row pipeline-node-level-3">
                <span className="pipeline-node-toggle empty">
                    <span className="material-icons">remove</span>
                </span>
                <span className="pipeline-node-icon level-bonds">
                    <span className="material-icons">account_balance</span>
                </span>
                <div className="pipeline-node-content">
                    <div className="pipeline-node-title">
                        {bond.isin} — {bond.issuer || t('common.unknown')}
                    </div>
                    <div className="pipeline-node-meta">
                        {bond.bond_type && <span>{bond.bond_type}</span>}
                        {bond.volume && bond.currency && (
                            <span>{bond.currency} {bond.volume}</span>
                        )}
                    </div>
                </div>
                <div className="pipeline-node-actions">
                    <Link
                        to={`/bonds/${bond._id}`}
                        className="pipeline-node-link"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {t('buttons.view')}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default SearchTargetPipelineView;
